import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWorkArea } from '@/contexts/WorkAreaContext';
import { useSession } from '@/hooks/useSession';
import {
  createApplicability,
  deleteApplicability,
  listApplicability,
  listCodebases,
  type Artifact,
  type ArtifactApplicability,
  type CodebaseResponse,
  type CreateArtifactApplicabilityBody,
} from '@/lib/api';
import { ConfirmActionDialog } from './ConfirmActionDialog';

interface ArtifactApplicabilityPanelProps {
  artifact: Artifact;
}

type ApplicabilityKind = ArtifactApplicability['kind'];

const APPLICABILITY_KIND_LABEL: Record<ApplicabilityKind, string> = {
  organization: 'Organization',
  work_area: 'Work Area',
  codebase: 'Codebase',
  reference_only: 'Reference-only',
};

function targetLabel(
  row: ArtifactApplicability,
  workAreas: ReturnType<typeof useWorkArea>['workAreas'],
  codebases: CodebaseResponse[]
): string {
  if (row.kind === 'organization') return 'Organization-wide';
  if (row.kind === 'reference_only') return 'Scope not decided';

  if (row.kind === 'work_area') {
    const name = workAreas?.find(workArea => workArea.id === row.workAreaId)?.name;
    return name ?? row.workAreaId ?? 'Unknown Work Area';
  }

  const name = codebases.find(codebase => codebase.id === row.codebaseId)?.name;
  return name ?? row.codebaseId ?? 'Unknown codebase';
}

export function ArtifactApplicabilityPanel({
  artifact,
}: ArtifactApplicabilityPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useSession();
  const isOwnerOrAdmin = isAdmin || (user !== null && user.id === artifact.ownerId);
  const { workAreas, isLoadingWorkAreas, isErrorWorkAreas, workAreasError } = useWorkArea();
  const codebasesQuery = useQuery({
    queryKey: ['codebases'],
    queryFn: listCodebases,
  });

  const applicabilityQuery = useQuery({
    queryKey: ['artifact', artifact.id, 'applicability'],
    queryFn: () => listApplicability(artifact.id),
  });

  const [kind, setKind] = useState<ApplicabilityKind>('organization');
  const [workAreaId, setWorkAreaId] = useState('');
  const [codebaseId, setCodebaseId] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ArtifactApplicability | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!workAreaId && workAreas && workAreas.length > 0) {
      setWorkAreaId(workAreas[0].id);
    }
  }, [workAreaId, workAreas]);

  useEffect(() => {
    const codebases = codebasesQuery.data;
    if (!codebaseId && codebases && codebases.length > 0) {
      setCodebaseId(codebases[0].id);
    }
  }, [codebaseId, codebasesQuery.data]);

  const createMutation = useMutation({
    mutationFn: (body: CreateArtifactApplicabilityBody) => createApplicability(artifact.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'applicability'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setCreateError(null);
    },
    onError: err => {
      setCreateError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (applicabilityId: string) => deleteApplicability(artifact.id, applicabilityId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'applicability'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setDeleteTarget(null);
      setDeleteError(null);
    },
    onError: err => {
      setDeleteError(err.message);
    },
  });

  function buildCreateBody(): CreateArtifactApplicabilityBody | null {
    if (kind === 'organization' || kind === 'reference_only') return { kind };

    if (kind === 'work_area') {
      if (!workAreaId) {
        setCreateError('Select a Work Area before adding applicability.');
        return null;
      }
      return { kind, work_area_id: workAreaId };
    }

    if (kind === 'codebase') {
      if (!codebaseId) {
        setCreateError('Select a codebase before adding applicability.');
        return null;
      }
      return { kind, codebase_id: codebaseId };
    }

    return null;
  }

  function handleCreate(): void {
    setCreateError(null);
    if (!isOwnerOrAdmin) return;
    const body = buildCreateBody();
    if (!body) return;
    createMutation.mutate(body);
  }

  const codebases = codebasesQuery.data ?? [];
  const rows = applicabilityQuery.data?.applicability ?? [];

  if (applicabilityQuery.isLoading) {
    return <div className="text-xs text-text-secondary">Loading applicability…</div>;
  }

  if (applicabilityQuery.isError) {
    return (
      <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
        {applicabilityQuery.error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-xs leading-5 text-text-secondary">
        Applicability records where this artifact applies. Use Work Area for product scope, Codebase
        for codebase-specific guidance, Organization for cross-codebase impact, or Reference-only
        when scope is not yet decided.
      </p>

      {rows.length === 0 ? (
        <span className="text-xs text-text-secondary">No applicability records yet.</span>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-left text-xs">
            <thead className="border-b border-border text-text-secondary">
              <tr>
                <th className="w-40 px-2 py-1 font-medium">Kind</th>
                <th className="px-2 py-1 font-medium">Target</th>
                <th className="w-24 px-2 py-1 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b border-border/60 last:border-0">
                  <td className="p-2 align-middle">
                    <span className="inline-flex max-w-full rounded-md bg-surface-elevated px-2 py-0.5 font-mono text-xs text-text-secondary">
                      <span className="truncate">{APPLICABILITY_KIND_LABEL[row.kind]}</span>
                    </span>
                  </td>
                  <td className="min-w-0 p-2 align-middle">
                    <span className="block truncate text-text-primary">
                      {targetLabel(row, workAreas, codebases)}
                    </span>
                    {row.note && (
                      <span className="block truncate text-text-secondary">{row.note}</span>
                    )}
                  </td>
                  <td className="p-2 text-right align-middle">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!isOwnerOrAdmin || deleteMutation.isPending}
                      onClick={(): void => {
                        setDeleteError(null);
                        setDeleteTarget(row);
                      }}
                      className="inline-flex items-center gap-1.5"
                    >
                      <Trash2 className="size-4" />
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col gap-2 border-t border-border pt-2">
        <span className="text-xs text-text-secondary">Add applicability</span>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(10rem,14rem)_minmax(0,1fr)_auto]">
          <select
            value={kind}
            onChange={event => {
              setKind(event.target.value as ApplicabilityKind);
              setCreateError(null);
            }}
            disabled={!isOwnerOrAdmin}
            className="min-w-0 rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            aria-label="Applicability kind"
          >
            <option value="organization">organization</option>
            <option value="work_area">work_area</option>
            <option value="codebase">codebase</option>
            <option value="reference_only">reference_only</option>
          </select>

          {kind === 'work_area' && (
            <select
              value={workAreaId}
              onChange={event => {
                setWorkAreaId(event.target.value);
              }}
              disabled={
                !isOwnerOrAdmin ||
                isLoadingWorkAreas ||
                isErrorWorkAreas ||
                (workAreas?.length ?? 0) === 0
              }
              className="min-w-0 rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
              aria-label="Work Area target"
            >
              {(workAreas ?? []).map(workArea => (
                <option key={workArea.id} value={workArea.id}>
                  {workArea.name}
                </option>
              ))}
            </select>
          )}

          {kind === 'codebase' && (
            <select
              value={codebaseId}
              onChange={event => {
                setCodebaseId(event.target.value);
              }}
              disabled={
                !isOwnerOrAdmin ||
                codebasesQuery.isLoading ||
                codebasesQuery.isError ||
                codebases.length === 0
              }
              className="min-w-0 rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
              aria-label="Codebase target"
            >
              {codebases.map(codebase => (
                <option key={codebase.id} value={codebase.id}>
                  {codebase.name}
                </option>
              ))}
            </select>
          )}

          {(kind === 'organization' || kind === 'reference_only') && (
            <span className="min-w-0 self-center truncate text-xs text-text-secondary">
              No target field required.
            </span>
          )}

          <Button
            size="sm"
            onClick={handleCreate}
            disabled={!isOwnerOrAdmin || createMutation.isPending}
            className="inline-flex items-center justify-center gap-1.5"
          >
            <Plus className="size-4" />
            {createMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>

        {isErrorWorkAreas && kind === 'work_area' && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {workAreasError?.message ?? 'Unable to load Work Areas'}
          </div>
        )}
        {codebasesQuery.isError && kind === 'codebase' && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {codebasesQuery.error.message}
          </div>
        )}
        {createError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {createError}
          </div>
        )}
      </div>

      <ConfirmActionDialog
        open={deleteTarget !== null}
        onOpenChange={next => {
          if (!next) setDeleteTarget(null);
        }}
        title="Remove applicability?"
        description={
          deleteTarget
            ? `This removes ${APPLICABILITY_KIND_LABEL[deleteTarget.kind]} applicability for ${targetLabel(
                deleteTarget,
                workAreas,
                codebases
              )}. The artifact is not deleted.`
            : ''
        }
        confirmText="Remove applicability"
        confirmTone="danger"
        onConfirm={(): void => {
          if (deleteTarget && isOwnerOrAdmin) deleteMutation.mutate(deleteTarget.id);
        }}
        pending={deleteMutation.isPending}
        errorMessage={deleteError}
      />
    </div>
  );
}
